import { useEffect, useRef, useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import api from '@/shared/api/client';
import enParallele from '../enParallele';

/**
 * LE MODE REMPLI : le raster du PDF RÉELLEMENT PRODUIT par les trois moteurs.
 *
 * ⚠️ POURQUOI UN VRAI RENDU SERVEUR ET NON UNE SIMULATION EN HTML
 *
 * L'écran pourrait dessiner les valeurs par-dessus le raster brut, dans les rectangles
 * des zones — ce serait instantané et sans requête. Ce serait aussi un MENSONGE : les
 * métriques de la police du navigateur ne sont pas celles de `helv` dans PyMuPDF, et
 * c'est PRÉCISÉMENT sur le débordement qu'on veut être cru.
 *
 * Un nom qui tient à l'écran et déborde dans le PDF est exactement le défaut que ce mode
 * existe pour montrer. Le montrer faux serait pire que ne pas le montrer.
 *
 * ⚠️ CE QU'UN ALLER-RETOUR COÛTE, MESURÉ LE 10/09 SUR LE DOCUMENT RÉEL (610 Ko) :
 *
 *     remplir_gabarit   165 ms      les trois moteurs
 *     raster PNG zoom 2  99 ms      294 Ko
 *     raster PNG zoom 1  41 ms      103 Ko
 *     HTTP + transfert  104 ms
 *     ────────────────────────
 *     total zoom 2      376 ms      293 Ko, à chaque frappe
 *
 * D'où les deux temps de ce composant : on rend VITE pendant qu'on tape, et NET quand
 * on s'arrête. L'image est étirée à la taille du cadre dans les deux cas ; entre deux
 * frappes, elle est simplement un peu moins fine — et personne ne lit du 6 pt en tapant.
 *
 * ⚠️ ET LE JPEG N'EST PAS LA RÉPONSE, C'EST MESURÉ. À zoom 2 : 239 ms et 334 Ko, contre
 * 99 ms et 294 Ko en PNG. Un mandat est du texte noir sur blanc — le pire cas du JPEG,
 * le meilleur du PNG. Ne pas « optimiser » dans ce sens.
 *
 * Pas d'ETag ici, contrairement au raster brut : le contenu dépend des valeurs qu'on
 * vient de taper. La route répond `no-store` pour la même raison.
 */

// Le premier rendu, pendant qu'on tape : le moins cher qui montre quelque chose.
const ZOOM_RAPIDE = 1;

// ⚠️ 400 ms, ET J'AVAIS D'ABORD MIS 800 — C'ÉTAIT UNE RÉGRESSION.
//
// L'intention était de couper des allers-retours pendant une saisie continue. Mesuré au
// navigateur : il n'y en avait AUCUN à couper. Le minuteur se réarme à chaque touche,
// donc taper sans s'arrêter ne déclenchait déjà aucun rendu. Les 300 ms ajoutées ne
// gagnaient rien et retardaient les premiers pixels de 876 à 1 050 ms.
//
// On descend donc SOUS l'ancienne valeur : le premier rendu est désormais léger — zoom 1,
// ~250 ms de serveur au lieu de 376 — il peut partir plus tôt sans coûter davantage.
const ATTENTE_MS = 400;

// Le second temps : on repasse en net quand plus rien ne bouge.
const ATTENTE_NETTE_MS = 1500;

// Le plafond de la route ; au-delà le serveur borne de toute façon.
const ZOOM_MAX = 3;

/**
 * Le zoom qu'il faut RÉELLEMENT pour cet écran, et pas un de plus.
 *
 * ⚠️ ZOOM 2 ÉTAIT UN CHIFFRE EN DUR, ET IL SUR-ÉCHANTILLONNAIT.
 *
 * Une A4 s'affiche ici dans ~462 px de large. Zoom 1 en rend déjà 596 ; zoom 2 en rend
 * 1 191 — deux fois et demie ce qu'un écran classique peut montrer, pour 292 Ko au lieu
 * de 102. Sur un écran à forte densité, en revanche, ces mêmes 462 px CSS valent 924
 * pixels réels, et le zoom 2 se justifie.
 *
 * On calcule donc au lieu de supposer : largeur affichée × densité ÷ largeur du
 * document. Le même code sert les deux écrans, et chacun ne paie que ce qu'il affiche.
 */
function zoomUtile(largeurAffichee, largeurPt) {
  if (!largeurAffichee || !largeurPt) return 2;
  const densite = typeof window === 'undefined' ? 1 : (window.devicePixelRatio || 1);
  return Math.max(1, Math.min(ZOOM_MAX,
    Math.ceil((largeurAffichee * densite) / largeurPt)));
}

export default function RenduRempli({ gabaritId, pages = [], valeurs, actif = true }) {
  const [images, setImages] = useState({});
  const [erreur, setErreur] = useState(null);
  const [enCours, setEnCours] = useState(false);
  const urls = useRef([]);
  // Le cadre de la première page : c'est lui qui dit de combien de pixels on a besoin.
  const premierCadre = useRef(null);

  const oublier = (garder = []) => {
    urls.current.filter((u) => !garder.includes(u)).forEach((u) => URL.revokeObjectURL(u));
    urls.current = garder;
  };

  useEffect(() => {
    // ⚠️ LE PANNEAU RESTE MONTÉ QUAND ON EST SUR LE PLAN — voir `MandatTemplatePage` :
    // le démonter faisait perdre les zones non enregistrées. Monté ne veut pas dire
    // actif : travailler pour un panneau que personne ne regarde coûte 376 ms et 293 Ko
    // par frappe.
    if (!actif || !gabaritId || !pages.length) return undefined;
    let vivant = true;

    const rendre = async (zoom) => {
      setEnCours(true);
      setErreur(null);
      try {
        const neuves = {};
        const nouvelles = [];
        // ⚠️ EN PARALLÈLE, MAIS TROIS À LA FOIS. Chaque page demandée est une
        // rastérisation serveur : les lancer toutes échangerait l'attente contre une
        // pointe de charge. Voir `enParallele`.
        await enParallele(pages, (page) => api.post(
          `/admin/mandat-templates/${gabaritId}/pages/${page.numero}/image-remplie`,
          valeurs, { params: { zoom }, responseType: 'blob' }), {
          arreter: () => !vivant,
          surChaque: (page, reponse) => {
            const url = URL.createObjectURL(new Blob([reponse.data], { type: 'image/png' }));
            nouvelles.push(url);
            neuves[page.numero] = url;
            // Chaque page s'affiche dès qu'elle arrive : un écran qui se remplit vaut
            // mieux qu'un écran vide jusqu'à la dernière.
            setImages((deja) => ({ ...deja, [page.numero]: url }));
          },
        });
        if (!vivant) { nouvelles.forEach((u) => URL.revokeObjectURL(u)); return; }
        oublier(nouvelles);
        setImages(neuves);
      } catch (e) {
        if (vivant) setErreur(e.response?.data?.detail || "Le rendu n'a pas pu être produit");
      }
      if (vivant) setEnCours(false);
    };

    const vite = setTimeout(() => rendre(ZOOM_RAPIDE), ATTENTE_MS);
    // ⚠️ LE SECOND RENDU N'A LIEU QUE S'IL APPORTE QUELQUE CHOSE.
    //
    // `zoomUtile` rend 1 sur un écran de densité 1 — la page y est déjà sur-échantillonnée
    // par le premier rendu. Relancer donnerait la MÊME image pour 250 ms de serveur et
    // autant de transfert. Sur un écran à forte densité il rend 2, et là il se justifie :
    // l'affinage est visible.
    //
    // Autrement dit : un écran classique ne fait plus qu'UN aller-retour de 102 Ko là où
    // il en faisait un de 293.
    const net = setTimeout(() => {
      const cadre = premierCadre.current?.getBoundingClientRect();
      const zoom = zoomUtile(cadre?.width, pages[0]?.largeur_pt);
      if (zoom > ZOOM_RAPIDE) rendre(zoom);
    }, ATTENTE_NETTE_MS);
    return () => { vivant = false; clearTimeout(vite); clearTimeout(net); };
  }, [gabaritId, pages, valeurs, actif]);

  useEffect(() => () => oublier(), []);

  if (erreur) {
    return (
      <div className="h-full flex items-center justify-center gap-2 text-[12px] text-amber-800"
           data-testid="gabarit-rempli-erreur">
        <AlertTriangle className="w-4 h-4 text-amber-600" /> {erreur}
      </div>
    );
  }

  return (
    // Même règle que le plan : la hauteur commande, la page se voit en entier.
    <div className="h-full min-h-0 overflow-auto p-3 flex flex-col items-center gap-3"
         data-testid="gabarit-rempli">
      {enCours && !Object.keys(images).length && (
        <div className="h-full flex items-center justify-center text-[12px] text-neutral-400">
          <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> Rendu réel en cours…
        </div>
      )}
      {pages.map((page) => (
        <div key={page.numero}
             ref={page.numero === 0 ? premierCadre : undefined}
             className="relative bg-white border border-neutral-200 rounded-md overflow-hidden
                        h-full w-auto max-w-full shrink-0"
             style={{ aspectRatio: `${page.largeur_pt} / ${page.hauteur_pt}` }}>
          {images[page.numero] && (
            <img src={images[page.numero]} alt={`Page ${page.numero + 1} remplie`}
                 className="w-full h-full block"
                 data-testid={`gabarit-rempli-page-${page.numero}`} />
          )}
        </div>
      ))}
    </div>
  );
}
